/**
 * Renderizador del lienzo. Usa dos canvas superpuestos:
 *  - "scene": figuras confirmadas (se redibuja al cambiar el documento o la cámara)
 *  - "overlay": trazo en curso, selección, cursores remotos y láser (se redibuja a 60 fps)
 */
import type { Presence, Shape } from '../../shared/types.ts'
import type { Box, Point } from '../lib/geom.ts'
import { boxCorners } from '../lib/geom.ts'
import type { BoardDoc } from '../state/board.ts'
import type { Camera, Store } from '../state/store.ts'
import { shapeBounds } from '../state/shapes.ts'
import { BACKGROUNDS, drawBackground } from './background.ts'
import { renderShape, setDarkDisplay } from './render-shape.ts'
import { drawMindMapEdges } from '../mindmap/edges.ts'
import { FONT_STACK } from './text.ts'

export type OverlayPainter = (ctx: CanvasRenderingContext2D, camera: Camera) => void

const HANDLE_SIZE = 9
const SELECTION_COLOR = '#0F6CBD'

export class Renderer {
  readonly scene: HTMLCanvasElement
  readonly overlay: HTMLCanvasElement
  private sceneCtx: CanvasRenderingContext2D
  private overlayCtx: CanvasRenderingContext2D
  private store: Store
  private board: BoardDoc
  private dpr = 1
  private width = 0
  private height = 0
  private sceneDirty = true
  private overlayDirty = true
  private frame = 0
  private painters = new Set<OverlayPainter>()
  private presenceProvider: () => Presence[] = () => []
  /** figuras ocultas por completo (p. ej. mientras el borrador las está quitando) */
  hidden = new Set<string>()
  /** figuras cuyo texto se oculta porque se está editando en el textarea flotante */
  textHidden = new Set<string>()

  constructor(scene: HTMLCanvasElement, overlay: HTMLCanvasElement, store: Store, board: BoardDoc) {
    this.scene = scene
    this.overlay = overlay
    this.store = store
    this.board = board
    this.sceneCtx = scene.getContext('2d', { alpha: false })!
    this.overlayCtx = overlay.getContext('2d')!
    this.resize()
    window.addEventListener('resize', this.resize)
    // El lienzo puede montarse después de construirse: el observer captura el
    // primer layout real y cualquier cambio de tamaño posterior.
    this.resizeObserver = new ResizeObserver(() => this.resize())
    if (scene.parentElement) this.resizeObserver.observe(scene.parentElement)
    this.loop()
  }

  private resizeObserver: ResizeObserver

  setPresenceProvider(fn: () => Presence[]): void {
    this.presenceProvider = fn
  }

  addPainter(painter: OverlayPainter): () => void {
    this.painters.add(painter)
    return () => this.painters.delete(painter)
  }

  resize = (): void => {
    const rect = this.scene.parentElement!.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    if (rect.width === this.width && rect.height === this.height && this.scene.width > 0) return
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5)
    this.width = rect.width
    this.height = rect.height
    for (const canvas of [this.scene, this.overlay]) {
      canvas.width = Math.floor(rect.width * this.dpr)
      canvas.height = Math.floor(rect.height * this.dpr)
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
    }
    this.invalidate()
  }

  get viewportSize(): { width: number; height: number } {
    return { width: this.width, height: this.height }
  }

  invalidate(): void {
    this.sceneDirty = true
    this.overlayDirty = true
  }

  invalidateOverlay(): void {
    this.overlayDirty = true
  }

  screenToWorld(x: number, y: number): Point {
    const { camera } = this.store
    return { x: x / camera.z + camera.x, y: y / camera.z + camera.y }
  }

  worldToScreen(x: number, y: number): Point {
    const { camera } = this.store
    return { x: (x - camera.x) * camera.z, y: (y - camera.y) * camera.z }
  }

  /** Rectángulo de mundo visible actualmente. */
  viewportBounds(pad = 0): Box {
    const { camera } = this.store
    return {
      x: camera.x - pad,
      y: camera.y - pad,
      w: this.width / camera.z + pad * 2,
      h: this.height / camera.z + pad * 2,
    }
  }

  private loop = (): void => {
    this.frame = requestAnimationFrame(this.loop)
    if (this.sceneDirty) {
      this.sceneDirty = false
      this.renderScene()
    }
    if (this.overlayDirty) {
      this.overlayDirty = false
      this.renderOverlay()
    }
  }

  private applyCamera(ctx: CanvasRenderingContext2D): void {
    const { camera } = this.store
    const s = this.dpr * camera.z
    ctx.setTransform(s, 0, 0, s, -camera.x * s, -camera.y * s)
  }

  renderScene(): void {
    const ctx = this.sceneCtx
    const { camera } = this.store
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    // Modo oscuro: el fondo elegido conserva su patrón pero en paleta negra,
    // y la tinta oscura se EXHIBE clara (los datos no cambian).
    setDarkDisplay(this.store.darkMode)
    let fondo = BACKGROUNDS[this.board.background] ?? BACKGROUNDS.dots
    if (this.store.darkMode && !fondo.dark) {
      fondo = { ...fondo, fill: '#161619', patternColor: '#33343A', dark: true }
    }
    drawBackground(ctx, fondo, camera, this.width, this.height)
    this.applyCamera(ctx)
    const view = this.viewportBounds(80 / camera.z)
    const all = this.board.all()
    // Conectores del mapa mental: debajo de todas las figuras.
    drawMindMapEdges(ctx, all)
    for (const shape of all) {
      if (this.hidden.has(shape.id)) continue
      if ((shape.type === 'topic' || shape.type === 'relation') && shape.hidden) continue
      const b = shapeBounds(shape)
      if (b.x + b.w < view.x || b.x > view.x + view.w || b.y + b.h < view.y || b.y > view.y + view.h) continue
      renderShape(ctx, shape, this.textHidden.has(shape.id))
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0)
  }

  /** Dibuja la escena completa en un contexto externo (exportar imagen). */
  renderTo(ctx: CanvasRenderingContext2D, bounds: Box, scale: number, background: boolean): void {
    // Exportar y miniaturas van SIEMPRE con los colores reales del tablero; al
    // terminar se devuelve el modo de exhibición de la vista.
    setDarkDisplay(false)
    try {
      this.renderToInner(ctx, bounds, scale, background)
    } finally {
      setDarkDisplay(this.store.darkMode)
    }
  }

  private renderToInner(ctx: CanvasRenderingContext2D, bounds: Box, scale: number, background: boolean): void {
    if (background) {
      const style = BACKGROUNDS[this.board.background] ?? BACKGROUNDS.dots
      ctx.fillStyle = style.fill
      ctx.fillRect(0, 0, bounds.w * scale, bounds.h * scale)
    }
    ctx.setTransform(scale, 0, 0, scale, -bounds.x * scale, -bounds.y * scale)
    const all = this.board.all()
    drawMindMapEdges(ctx, all)
    for (const shape of all) {
      if ((shape.type === 'topic' || shape.type === 'relation') && shape.hidden) continue
      renderShape(ctx, shape)
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0)
  }

  private renderOverlay(): void {
    const ctx = this.overlayCtx
    const { camera } = this.store
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, this.overlay.width, this.overlay.height)

    this.applyCamera(ctx)
    this.drawSelection(ctx)
    for (const painter of this.painters) painter(ctx, camera)
    this.drawPresence(ctx)
    ctx.setTransform(1, 0, 0, 1, 0, 0)
  }

  private drawSelection(ctx: CanvasRenderingContext2D): void {
    const { selection, camera, editingId } = this.store
    if (selection.length === 0) return
    const shapes = selection.map((id) => this.board.get(id)).filter(Boolean) as Shape[]
    if (shapes.length === 0) return
    const inv = 1 / camera.z

    ctx.save()
    ctx.strokeStyle = SELECTION_COLOR
    ctx.lineWidth = 1.5 * inv

    // Contorno girado solo como referencia visual: las manijas van SIEMPRE sobre
    // el rectángulo recto, que es el que responde al clic. Antes se dibujaban
    // sobre las esquinas giradas y no coincidían con la zona sensible.
    if (shapes.length === 1 && shapes[0].rotation) {
      const shape = shapes[0]
      const corners = boxCorners({ x: shape.x, y: shape.y, w: shape.w, h: shape.h }, shape.rotation)
      ctx.save()
      ctx.globalAlpha = 0.5
      ctx.setLineDash([4 * inv, 3 * inv])
      ctx.beginPath()
      ctx.moveTo(corners[0].x, corners[0].y)
      for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y)
      ctx.closePath()
      ctx.stroke()
      ctx.restore()
    }

    const box = this.selectionBounds()
    if (!box) {
      ctx.restore()
      return
    }
    // Contorno individual tenue cuando hay varias figuras seleccionadas.
    if (shapes.length > 1) {
      ctx.save()
      ctx.globalAlpha = 0.45
      ctx.setLineDash([4 * inv, 3 * inv])
      for (const shape of shapes) {
        const b = shapeBounds(shape)
        ctx.strokeRect(b.x, b.y, b.w, b.h)
      }
      ctx.restore()
    }
    ctx.strokeRect(box.x, box.y, box.w, box.h)
    // Los temas y relaciones del mapa no se escalan ni rotan a mano: su
    // geometría la pone el layout, así que no se ofrecen manijas.
    const conManijas =
      !editingId && !shapes.some((s) => s.type === 'topic' || s.type === 'relation')
    if (conManijas) {
      this.drawHandle(ctx, box.x, box.y, inv)
      this.drawHandle(ctx, box.x + box.w, box.y, inv)
      this.drawHandle(ctx, box.x + box.w, box.y + box.h, inv)
      this.drawHandle(ctx, box.x, box.y + box.h, inv)
      // Manija de rotación
      const rotY = box.y - 26 * inv
      ctx.beginPath()
      ctx.moveTo(box.x + box.w / 2, box.y)
      ctx.lineTo(box.x + box.w / 2, rotY)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(box.x + box.w / 2, rotY, 5 * inv, 0, Math.PI * 2)
      ctx.fillStyle = '#FFFFFF'
      ctx.fill()
      ctx.stroke()
    }
    ctx.restore()
  }

  private drawHandle(ctx: CanvasRenderingContext2D, x: number, y: number, inv: number): void {
    const size = HANDLE_SIZE * inv
    ctx.fillStyle = '#FFFFFF'
    ctx.beginPath()
    ctx.roundRect(x - size / 2, y - size / 2, size, size, 2 * inv)
    ctx.fill()
    ctx.stroke()
  }

  selectionBounds(): Box | null {
    const shapes = this.store.selection.map((id) => this.board.get(id)).filter(Boolean) as Shape[]
    if (shapes.length === 0) return null
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const shape of shapes) {
      const b = shapeBounds(shape)
      minX = Math.min(minX, b.x)
      minY = Math.min(minY, b.y)
      maxX = Math.max(maxX, b.x + b.w)
      maxY = Math.max(maxY, b.y + b.h)
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
  }

  private drawPresence(ctx: CanvasRenderingContext2D): void {
    const { camera } = this.store
    const inv = 1 / camera.z
    for (const peer of this.presenceProvider()) {
      if (peer.laser && peer.laser.length >= 4) this.drawLaser(ctx, peer.laser, peer.color, inv)
      if (!peer.cursor) continue
      const { x, y } = peer.cursor
      ctx.save()
      ctx.translate(x, y)
      ctx.scale(inv, inv)
      // Puntero
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(0, 17)
      ctx.lineTo(4.6, 12.8)
      ctx.lineTo(7.6, 19.2)
      ctx.lineTo(10.4, 17.8)
      ctx.lineTo(7.4, 11.6)
      ctx.lineTo(13, 11)
      ctx.closePath()
      ctx.fillStyle = peer.color
      ctx.strokeStyle = '#FFFFFF'
      ctx.lineWidth = 1.4
      ctx.fill()
      ctx.stroke()
      // Etiqueta con el nombre
      const label = peer.name || 'Invitado'
      ctx.font = `600 12px ${FONT_STACK}`
      const w = ctx.measureText(label).width + 16
      ctx.beginPath()
      ctx.roundRect(13, 16, w, 22, 11)
      ctx.fillStyle = peer.color
      ctx.fill()
      ctx.fillStyle = '#FFFFFF'
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'left'
      ctx.fillText(label, 21, 27.5)
      ctx.restore()
    }
  }

  private drawLaser(ctx: CanvasRenderingContext2D, points: number[], color: string, inv: number): void {
    ctx.save()
    ctx.strokeStyle = color
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.shadowColor = color
    ctx.shadowBlur = 12 * inv
    const n = points.length / 2
    for (let i = 1; i < n; i++) {
      const t = i / n
      ctx.globalAlpha = t * 0.85
      ctx.lineWidth = (2 + t * 4) * inv
      ctx.beginPath()
      ctx.moveTo(points[(i - 1) * 2], points[(i - 1) * 2 + 1])
      ctx.lineTo(points[i * 2], points[i * 2 + 1])
      ctx.stroke()
    }
    ctx.restore()
  }

  destroy(): void {
    cancelAnimationFrame(this.frame)
    window.removeEventListener('resize', this.resize)
    this.resizeObserver.disconnect()
  }
}
